import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { InstructivoEmbarqueStateService } from '../../../services/instructivo-embarque-state.service';
import { InstructivoEmbarqueService } from '../../../services/instructivo-embarque.service';
import {
    ApplyBookingResponseDto,
    BookingItemPreloadDto,
    BookingSummaryDto,
    PartyDetailDto,
} from '@opb-workspace/shared';
import {
    bookingNumberDisplayFromSummary,
    clearStaleBookingSelection,
    createDisplayBookingSummaryFn,
    filterBookingSummaries,
    searchTextFromBookingInputValue,
} from './step2-booking-summary.util';
import {
    applyBookingAutocompleteSelection,
    resolveBookingSummaryFromOptionValue,
} from './step2-booking-autocomplete-selection.util';
import {
    applySecondColumnAfterBookingBlur,
    resolvePortalClientDisplayName,
} from './step2-booking-second-column.util';
import { refreshPortalClientNameInSecondColumn } from './step2-refresh-portal-client-name.util';
import { restoreStep2FormFromWizardSnapshot } from './step2-restore-from-snapshot.util';
import { pushStep2FormToWizardState } from './step2-push-form-to-state.util';
import {
    applyStep2PartySelection,
    isUserPartyOptionSelect,
    PartyRole,
    runStep2PartySearch,
} from './step2-party-actions.util';
import { syncPartiesFromApplyBooking } from './step2-sync-parties-from-booking.util';
import { hydrateStep2SelectedPartiesFromForm } from './step2-hydrate-selected-parties-from-form.util';
import { findOpbClientShipperInRows } from './step2-opb-default-client-shipper.util';
import { AuthService } from '../../../../../services/auth.service';
import { ClientService } from '../../../../../services/client.service';

@Component({
    selector: 'app-step2-booking-organizaciones',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatCheckboxModule,
    ],
    templateUrl: './step2-booking-organizaciones.component.html',
    styleUrls: ['./step2-booking-organizaciones.component.scss'],
})
export class Step2BookingOrganizacionesComponent implements OnInit {
    @ViewChild(MatAutocompleteTrigger)
    bookingAutocompleteTrigger?: MatAutocompleteTrigger;

    form!: FormGroup;
    bookingInputCtrl = new FormControl<string | BookingSummaryDto>('', {
        nonNullable: true,
    });
    exporterFromBookingCtrl = new FormControl('', { nonNullable: true });
    filteredBookings: BookingSummaryDto[] = [];
    availableBookings: BookingSummaryDto[] = [];
    bookingApplied: ApplyBookingResponseDto | null = null;
    availableContainerItems: BookingItemPreloadDto[] = [];
    selectedContainerIds = new Set<number>();
    isOpbBooking = false;
    hasActiveShippingInstruction = false;
    isLoading = false;
    blockingWarning = '';
    /** Evita aplicar respuesta vieja si el usuario cambia de booking rápido. */
    private applyBookingRequestSeq = 0;

    selectedShipper: PartyDetailDto | null = null;

    shipperOptions: PartyDetailDto[] = [];

    displayBookingSummaryForAutocomplete = createDisplayBookingSummaryFn(
        () => this.availableBookings,
    );

    /** Botón "Aplicar booking" oculto en UI; la precarga corre al elegir booking en el autocomplete. */
    get showApplyBookingButton(): boolean {
        return false;
    }

    /**
     * true si el usuario no ingresó ni seleccionó ningún booking (ni OPB ni externo).
     * Considera tanto el valor ya sincronizado en el form (`bookingNumber`) como el texto
     * crudo del autocomplete, por si todavía no hubo blur al momento de avanzar de paso.
     */
    get isBookingEmpty(): boolean {
        const fromForm = (this.form.get('bookingNumber')?.value ?? '').toString().trim();
        if (fromForm) {
            return false;
        }
        const fromInput = searchTextFromBookingInputValue(
            this.bookingInputCtrl.value,
            this.availableBookings,
        ).trim();
        return !fromInput;
    }

    /**
     * Texto de ayuda para booking no OPB: hay búsqueda sin selección y ningún resultado en listado OPB.
     */
    get showNonOpbBookingHint(): boolean {
        if (this.form.get('selectedBookingId')?.value != null) {
            return false;
        }
        const q = searchTextFromBookingInputValue(
            this.bookingInputCtrl.value,
            this.availableBookings,
        ).trim();
        if (!q.length) {
            return false;
        }
        return this.filteredBookings.length === 0;
    }

    constructor(
        private fb: FormBuilder,
        public stateService: InstructivoEmbarqueStateService,
        private service: InstructivoEmbarqueService,
        private authService: AuthService,
        private clientService: ClientService,
        private snackBar: MatSnackBar
    ) {
        this.initForm();
    }

    private initForm(): void {
        this.form = this.fb.group({
            selectedBookingId: [null],
            bookingNumber: [''],
            customerContactEmail: [''],
            customerAttentionContactName: [''],
            customerTaxIdentificationNumber: [''],
            shipperPartyId: [null, Validators.required],
            shipperName: ['', Validators.required],
            shipperAddress: [''],
            shipperEmail: [''],
            shipperTaxIdentificationNumber: [''],
            shipperContactName: [''],
            shipperPhoneNumber: [''],
            shipperMobilePhoneNumber: [''],
        });
    }

    ngOnInit(): void {
        const voyageData = this.stateService.snapshot.voyageData;
        if (voyageData?.availableBookings) {
            this.availableBookings = voyageData.availableBookings;
            this.filteredBookings = [...this.availableBookings];
        }

        const res = restoreStep2FormFromWizardSnapshot(
            this.form,
            this.bookingInputCtrl,
            this.stateService.snapshot.formData,
            this.availableBookings,
        );
        if (res.mode === 'booking') {
            const snap = this.stateService.snapshot;
            this.bookingApplied = snap.bookingData || null;
            this.isOpbBooking = this.bookingApplied?.isOpbBooking || false;
            const persistedShipperId = snap.formData.shipperPartyId;
            if (persistedShipperId && this.bookingApplied) {
                hydrateStep2SelectedPartiesFromForm(this.form, this);
                this.refreshPortalClienteSecondColumn();
            } else {
                this.preloadParties();
            }
        } else if (res.mode === 'external-booking') {
            this.bookingApplied = null;
            this.isOpbBooking = false;
            if (this.stateService.snapshot.bookingData) {
                this.stateService.clearBookingApplySnapshot();
            }
        }
        this.refreshPortalClienteSecondColumn();
    }

    private resolvePortalNameSync(): string {
        return resolvePortalClientDisplayName(
            this.authService.getCurrentUser(),
            this.stateService.snapshot.formData.customerName,
        );
    }

    /** `clientId` del JWT (CustomerId portal); null si no aplica. */
    private resolvePortalCustomerId(): number | null {
        const raw = this.authService.getCurrentUser()?.clientId;
        if (raw == null) {
            return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? Math.trunc(n) : null;
    }

    private refreshPortalClienteSecondColumn(): void {
        const user = this.authService.getCurrentUser();
        const idRaw = user?.clientId != null ? Number(user.clientId) : Number.NaN;
        refreshPortalClientNameInSecondColumn(
            this.clientService,
            this.exporterFromBookingCtrl,
            this.resolvePortalNameSync(),
            Number.isFinite(idRaw) ? idRaw : null,
        );
    }

    onBookingInputBlur(): void {
        let attempts = 0;
        const run = (): void => {
            attempts++;
            if (this.bookingAutocompleteTrigger?.panelOpen && attempts < 16) {
                setTimeout(run, 40);
                return;
            }
            applySecondColumnAfterBookingBlur({
                form: this.form,
                bookingInputCtrl: this.bookingInputCtrl,
                availableBookings: this.availableBookings,
                exporterCtrl: this.exporterFromBookingCtrl,
                portalClientName: this.resolvePortalNameSync(),
            });
            this.refreshPortalClienteSecondColumn();
            this.syncBookingAppliedWithBookingSelection();
        };
        setTimeout(run, 0);
    }

    onBookingSearchInput(): void {
        const searchText = searchTextFromBookingInputValue(
            this.bookingInputCtrl.value,
            this.availableBookings,
        );
        clearStaleBookingSelection(
            this.form,
            this.availableBookings,
            searchText,
        );
        if (this.form.get('selectedBookingId')?.value == null) {
            this.exporterFromBookingCtrl.setValue(this.resolvePortalNameSync(), {
                emitEvent: false,
            });
        }
        this.filteredBookings = filterBookingSummaries(
            this.availableBookings,
            searchText,
        );
        this.syncBookingAppliedWithBookingSelection();
    }

    /**
     * Si ya no hay booking OPB seleccionado, oculta el mensaje verde y limpia el snapshot del wizard.
     */
    private syncBookingAppliedWithBookingSelection(): void {
        const sid = this.form.get('selectedBookingId')?.value;
        if (sid != null) {
            return;
        }
        if (
            this.bookingApplied == null &&
            !this.isOpbBooking &&
            !this.stateService.snapshot.bookingData
        ) {
            return;
        }
        this.bookingApplied = null;
        this.availableContainerItems = [];
        this.selectedContainerIds = new Set();
        this.isOpbBooking = false;
        this.stateService.clearBookingApplySnapshot();
    }

    onBookingSelected(ev: MatAutocompleteSelectedEvent): void {
        const raw = ev.option?.value as BookingSummaryDto | null | undefined;
        const v = resolveBookingSummaryFromOptionValue(
            raw,
            this.availableBookings,
        );
        const r = applyBookingAutocompleteSelection(
            this.form,
            this.bookingInputCtrl,
            this.exporterFromBookingCtrl,
            v,
            this.resolvePortalNameSync(),
        );
        if (r === 'cleared') {
            this.filteredBookings = [...this.availableBookings];
        }
        if (r === 'selected') {
            this.runApplyBookingPipeline(false);
        }
        this.refreshPortalClienteSecondColumn();
    }

    applyBooking(): void {
        this.runApplyBookingPipeline(true);
    }

    /**
     * Precarga shipper/consignee/notify vía API al elegir booking OPB (o al pulsar Aplicar).
     * @param showAlertIfNoBooking — solo true cuando el usuario pulsa el botón sin selección.
     */
    private runApplyBookingPipeline(showAlertIfNoBooking: boolean): void {
        const bookingId = this.form.get('selectedBookingId')?.value;
        if (!bookingId) {
            if (showAlertIfNoBooking) {
                alert('Por favor, seleccione una reserva.');
            }
            return;
        }

        this.isLoading = true;
        this.blockingWarning = '';
        const seq = ++this.applyBookingRequestSeq;
        this.service
            .applyBooking({
                voyageId: this.stateService.snapshot.voyageData?.voyageId || 0,
                bookingId,
            })
            .subscribe({
                next: (response) => {
                    if (seq !== this.applyBookingRequestSeq) {
                        return;
                    }
                    this.hasActiveShippingInstruction = Boolean(response.hasActiveShippingInstruction);
                    if (response.hasAvailableContainers === false && response.isOpbBooking) {
                        this.blockingWarning =
                            'Esta reserva no tiene contenedores disponibles. Todos los contenedores ya están asignados a instructivos activos.';
                        this.isLoading = false;
                        return;
                    }
                    this.bookingApplied = response;
                    this.isOpbBooking = response.isOpbBooking;
                    const listRow = this.availableBookings.find(
                        (b) => b.bookingId === bookingId,
                    );
                    response.pendingContainerCount = listRow?.pendingContainerCount;
                    this.stateService.applyBookingData(response);
                    this.availableContainerItems = (response.items ?? []).filter(i => i.bookingContainerId != null);
                    this.selectedContainerIds = new Set(this.availableContainerItems.map(i => i.bookingContainerId!));
                    const resolvedBookingNumber =
                        (response.bookingNumber ?? '').trim() ||
                        (listRow
                            ? bookingNumberDisplayFromSummary(listRow)
                            : '');
                    if (resolvedBookingNumber) {
                        this.stateService.patchFormData({
                            bookingNumber: resolvedBookingNumber,
                        });
                    }
                    this.form.patchValue({
                        bookingNumber: resolvedBookingNumber,
                    });
                    this.preloadParties();
                    this.isLoading = false;
                },
                error: () => {
                    if (seq !== this.applyBookingRequestSeq) {
                        return;
                    }
                    alert('No se pudo aplicar la reserva.');
                    this.isLoading = false;
                },
            });
    }

    private preloadParties(): void {
        if (!this.bookingApplied) return;
        const opb = this.bookingApplied.isOpbBooking;
        const p = syncPartiesFromApplyBooking(this.form, this.bookingApplied, {
            omitShipper: opb,
        });
        this.selectedShipper = opb ? null : p.shipper;
        this.refreshPortalClienteSecondColumn();
        if (opb) {
            this.applyClientAsDefaultExporter();
        } else {
            this.selectedShipper = p.shipper;
            pushStep2FormToWizardState(
                this.stateService,
                this.form,
                this.bookingInputCtrl,
                this.availableBookings,
            );
        }
    }

    /**
     * Para bookings OPB: aplica el cliente (CustomerId del booking) como exportador por
     * defecto. La selección es inmediata con los datos del apply-booking; luego hace un
     * GET /parties para obtener la fila real de GetShippers si existe (enriquecimiento).
     */
    private applyClientAsDefaultExporter(): void {
        const applied = this.bookingApplied!;
        const portalCustomerId = this.resolvePortalCustomerId();
        const bid = this.form.get('selectedBookingId')?.value as number | null;
        const fromList = this.availableBookings.find(
            (b) => b.bookingId === bid,
        );
        const fallbackName =
            (fromList?.customerName ?? '').trim() ||
            this.resolvePortalNameSync().trim() ||
            '';

        const clientParty: PartyDetailDto = {
            partyId: applied.customerId > 0
                ? applied.customerId
                : (portalCustomerId ?? 0),
            name: applied.customerName || fallbackName,
            shortName: applied.customerName || fallbackName,
            fullName: applied.customerName || fallbackName,
            address: applied.customerAddress,
            email: applied.customerContactEmail,
            taxIdentificationNumber: applied.customerTaxIdentificationNumber,
            contactName: applied.customerAttentionContactName,
        };

        if (clientParty.partyId > 0 || clientParty.name) {
            applyStep2PartySelection(this.form, 'shipper', clientParty, this);
        }

        this.refreshPortalClienteSecondColumn();

        // Enriquecimiento: busca la fila real en GetShippers para actualizar datos
        this.service.searchParties('shipper', '').subscribe({
            next: (rows) => {
                const match = findOpbClientShipperInRows(
                    rows,
                    applied,
                    portalCustomerId,
                    fallbackName,
                );
                if (match) {
                    applyStep2PartySelection(this.form, 'shipper', match, this);
                }
                // Poblar la lista completa para que el usuario pueda cambiar el exportador
                const sel = this.selectedShipper;
                this.shipperOptions =
                    sel && !rows.some((r) => r.partyId === sel.partyId)
                        ? [sel, ...rows]
                        : rows;
                this.refreshPortalClienteSecondColumn();
                pushStep2FormToWizardState(
                    this.stateService,
                    this.form,
                    this.bookingInputCtrl,
                    this.availableBookings,
                );
            },
            error: () => {
                pushStep2FormToWizardState(
                    this.stateService,
                    this.form,
                    this.bookingInputCtrl,
                    this.availableBookings,
                );
            },
        });
    }

    searchParties(role: PartyRole, searchTerm: string): void {
        if (role !== 'shipper') return;
        runStep2PartySearch(
            this.service,
            role,
            searchTerm,
            this,
            () => {
                this.snackBar.open(
                    'No se pudieron cargar los exportadores. Compruebe la API/SQL (sp_InstructivoEmbarque_GetShippers).',
                    'Cerrar',
                    { duration: 8000 },
                );
            },
        );
    }

    selectParty(role: PartyRole, party: PartyDetailDto): void {
        applyStep2PartySelection(this.form, role, party, this);
        pushStep2FormToWizardState(
            this.stateService,
            this.form,
            this.bookingInputCtrl,
            this.availableBookings,
        );
    }

    onPartyOptionSelectionChange(
        ev: MatOptionSelectionChange,
        role: PartyRole,
        party: PartyDetailDto,
    ): void {
        if (!isUserPartyOptionSelect(ev)) {
            return;
        }
        this.selectParty(role, party);
    }

    saveStep(): void {
        if (this.form.invalid) {
            alert('Por favor, complete todos los campos obligatorios.');
            return;
        }
        pushStep2FormToWizardState(
            this.stateService,
            this.form,
            this.bookingInputCtrl,
            this.availableBookings,
        );
    }

    isContainerSelected(id: number): boolean {
        return this.selectedContainerIds.has(id);
    }

    toggleContainer(id: number): void {
        if (this.selectedContainerIds.has(id)) {
            this.selectedContainerIds.delete(id);
        } else {
            this.selectedContainerIds.add(id);
        }
    }

    /**
     * Persiste booking, ref. cliente y partes en el estado del wizard.
     * Debe llamarse al salir del paso 2 (p. ej. desde el stepper).
     */
    syncFormToWizardState(): void {
        if (this.isOpbBooking && this.availableContainerItems.length > 0 && this.selectedContainerIds.size > 0) {
            const currentItems = this.stateService.snapshot.formData.items ?? [];
            const filtered = currentItems.filter(
                item => item.bookingContainerId == null || this.selectedContainerIds.has(item.bookingContainerId as number)
            );
            if (filtered.length !== currentItems.length) {
                this.stateService.patchFormData({ items: filtered });
            }
        }
        pushStep2FormToWizardState(
            this.stateService,
            this.form,
            this.bookingInputCtrl,
            this.availableBookings,
        );
    }
}
